import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getAuditLog } from "@/lib/audit-log.functions";

const ENTITY_LABELS: Record<string, string> = {
  subscription: "Naročnina",
  recipient_label: "Oznaka prejemnika",
  suppression: "Blokada pošiljanja",
};

const ACTION_LABELS: Record<string, string> = {
  created: "Dodano",
  updated: "Spremenjeno",
  deleted: "Odstranjeno",
};

const FILTERS: { key: string; label: string }[] = [
  { key: "all", label: "Vse" },
  { key: "subscription", label: "Naročnine" },
  { key: "recipient_label", label: "Oznake" },
  { key: "suppression", label: "Blokade" },
];

function formatDate(iso: string) {
  const d = new Date(iso);
  return `${d.toLocaleDateString("sl-SI", { day: "2-digit", month: "2-digit", year: "numeric" })} ob ${d.toLocaleTimeString("sl-SI", { hour: "2-digit", minute: "2-digit" })}`;
}

export function AuditLogSection() {
  const fetchLog = useServerFn(getAuditLog);
  const [entity, setEntity] = useState("all");
  const [emailInput, setEmailInput] = useState("");
  const [email, setEmail] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["audit-log", entity, email],
    queryFn: () => fetchLog({ data: { entity, email, limit: 300 } }),
  });

  const rows = data?.rows ?? [];

  return (
    <div>
      <p className="text-sm text-muted-foreground">
        Samodejna zgodovina sprememb naročnin, oznak prejemnikov in blokad pošiljanja. Pomaga hitro razložiti,
        zakaj je bilo sporočilo poslano ali ne.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setEntity(f.key)}
            className={`border px-3 py-2 text-sm ${entity === f.key ? "border-accent text-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            {f.label}
          </button>
        ))}
        <form
          className="flex flex-1 min-w-[220px] gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setEmail(emailInput);
          }}
        >
          <label className="sr-only" htmlFor="audit-email">Iskanje po e-naslovu</label>
          <input
            id="audit-email"
            type="search"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="Iskanje po e-naslovu"
            className="flex-1 border border-border px-3 py-2 text-sm"
          />
          <button type="submit" className="border border-brand text-brand px-4 py-2 text-sm hover:bg-brand/5">Išči</button>
        </form>
      </div>

      {isLoading && <div className="mt-6 text-sm text-muted-foreground">Nalagam…</div>}
      {error && <div className="mt-6 text-sm text-destructive">Napaka: {(error as Error).message}</div>}

      {!isLoading && !error && rows.length === 0 && (
        <div className="mt-6 text-sm text-muted-foreground">Ni zapisov za izbrani filter.</div>
      )}

      {rows.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-4 font-normal">Čas</th>
                <th className="py-2 pr-4 font-normal">Področje</th>
                <th className="py-2 pr-4 font-normal">Dogodek</th>
                <th className="py-2 pr-4 font-normal">E-naslov</th>
                <th className="py-2 font-normal">Opis</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60 align-top">
                  <td className="py-2 pr-4 whitespace-nowrap">{formatDate(r.created_at)}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">{ENTITY_LABELS[r.entity] ?? r.entity}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">{ACTION_LABELS[r.action] ?? r.action}</td>
                  <td className="py-2 pr-4 break-all">{r.email ?? "—"}</td>
                  <td className="py-2">{r.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
